/**
 * Subject Lottie Icon Configuration
 * 
 * Maps CourseSubject enum values to locally stored Lottie animation files.
 * Animations are stored in src/images/lottie/
 */

import { CourseSubject } from '@/types/firebase'

export interface SubjectLottieConfig {
  /**
   * Path to Lottie JSON file (relative to src/images/lottie/)
   */
  fileName: string
  
  /**
   * Primary brand color for this subject (used in card styling)
   */
  primaryColor: string
  
  /**
   * Secondary accent color
   */
  secondaryColor?: string
}

/**
 * Subject-specific Lottie animation configuration
 */
export const SUBJECT_LOTTIE_ICONS: Record<CourseSubject, SubjectLottieConfig> = {
  [CourseSubject.Maths]: {
    fileName: 'math.json',
    primaryColor: '#3b82f6', // Blue
    secondaryColor: '#60a5fa',
  },
  
  [CourseSubject.Physics]: {
    fileName: 'physics.json',
    primaryColor: '#8b5cf6', // Purple
    secondaryColor: '#a78bfa',
  },
  
  [CourseSubject.Chemistry]: {
    fileName: 'chemistry.json',
    primaryColor: '#10b981', // Green
    secondaryColor: '#34d399',
  },
  
  [CourseSubject.Biology]: {
    fileName: 'biology.json',
    primaryColor: '#22c55e', // Light green
    secondaryColor: '#4ade80',
  },
  
  [CourseSubject.EnglishLiterature]: {
    fileName: 'eng-literature.json',
    primaryColor: '#f59e0b', // Amber
    secondaryColor: '#fbbf24',
  },
  
  [CourseSubject.EnglishLanguage]: {
    fileName: 'eng-language.json',
    primaryColor: '#f97316', // Orange
    secondaryColor: '#fb923c',
  },
  
  [CourseSubject.Geography]: {
    fileName: 'geography.json',
    primaryColor: '#06b6d4', // Cyan
    secondaryColor: '#22d3ee',
  },
  
  [CourseSubject.History]: {
    fileName: 'history.json',
    primaryColor: '#be185d', // Rose
    secondaryColor: '#e11d48',
  },
  
  [CourseSubject.Psychology]: {
    fileName: 'psychology.json',
    primaryColor: '#ec4899', // Pink
    secondaryColor: '#f472b6',
  },
}

/**
 * Get Lottie animation config for a subject
 */
export function getSubjectLottieIcon(subject: CourseSubject): SubjectLottieConfig {
  return SUBJECT_LOTTIE_ICONS[subject] || SUBJECT_LOTTIE_ICONS[CourseSubject.Maths]
}

/**
 * Get full path to Lottie animation file
 */
export function getSubjectLottiePath(subject: CourseSubject): string {
  const config = getSubjectLottieIcon(subject)
  return `/images/lottie/${config.fileName}`
}

